import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Globe, Shield, CheckCircle2 } from "lucide-react";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { register } = useAuth();
  const { t, language, changeLanguage, languages } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await register(email, password, name);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Language Selector - Fixed top right */}
      <div className="absolute top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-sm font-medium text-white hover:bg-white/20 transition-all"
            data-testid="language-selector"
          >
            <Globe className="h-4 w-4" />
            {languages.find((l) => l.code === language)?.name || language.toUpperCase()}
          </button>
          {langOpen && (
            <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white dark:bg-zinc-900 border border-border shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLanguage(lang.code); setLangOpen(false); }}
                  className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-muted ${
                    language === lang.code ? "text-primary bg-primary/5" : "text-foreground"
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  {lang.flag} {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Left side - Premium Hero */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 30% 20%, #1a3a5c 0%, #0B1F3A 50%, #061020 100%)" }}>
        {/* Glow effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-[#2a5a8c]/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[300px] bg-[#1a3a5c]/20 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 text-white max-w-lg">
          <h1 className="text-4xl xl:text-5xl font-black tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
            {t("auth.heroTitle")}
          </h1>
          <p className="text-lg text-white/70 leading-relaxed mb-4" data-testid="hero-subtitle">
            {t("auth.heroSubtitle")}
          </p>
          <p className="text-base text-white/50 leading-relaxed mb-12" data-testid="hero-desc">
            {t("auth.heroDesc")}
          </p>

          {/* Stats */}
          <div className="flex items-center gap-8 mb-10">
            <div>
              <p className="text-3xl xl:text-4xl font-black text-white">{t("auth.creatorsCount")}</p>
              <p className="text-sm text-white/50 mt-1">{t("auth.creatorsLabel")}</p>
            </div>
            <div className="w-px h-14 bg-white/15" />
            <div>
              <p className="text-3xl xl:text-4xl font-black text-white">{t("auth.paymentsCount")}</p>
              <p className="text-sm text-white/50 mt-1">{t("auth.paymentsLabel")}</p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-white/60">
              <Shield className="h-4 w-4 text-emerald-400" />
              <span className="text-sm font-medium">{t("auth.securePayments")}</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <span className="text-sm font-medium">{t("auth.trustedPlatform")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Hero */}
      <div className="lg:hidden px-6 pt-16 pb-8 text-center"
        style={{ background: "radial-gradient(ellipse at center, #1a3a5c 0%, #0B1F3A 50%, #061020 100%)" }}>
        <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-3">
          {t("auth.heroTitle")}
        </h1>
        <p className="text-sm text-white/60 leading-relaxed mb-5 max-w-md mx-auto">
          {t("auth.heroSubtitle")}
        </p>
        <div className="flex items-center justify-center gap-6">
          <div>
            <p className="text-xl font-bold text-white">{t("auth.creatorsCount")}</p>
            <p className="text-xs text-white/50">{t("auth.creatorsLabel")}</p>
          </div>
          <div className="w-px h-10 bg-white/15" />
          <div>
            <p className="text-xl font-bold text-white">{t("auth.paymentsCount")}</p>
            <p className="text-xs text-white/50">{t("auth.paymentsLabel")}</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8">
        <Card className="w-full max-w-md border-border shadow-2xl shadow-[#0B1F3A]/10 dark:shadow-white/5">
          <CardHeader className="space-y-1 text-center pb-2">
            <div className="flex justify-center mb-6">
              <span className="text-3xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
                Ellalink
              </span>
            </div>
            <CardTitle className="text-2xl font-bold">{t("auth.createAccount")}</CardTitle>
            <CardDescription className="text-base">{t("auth.registerSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm animate-in fade-in slide-in-from-top-2 duration-300">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">{t("auth.name")}</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="name" type="text" placeholder={t("auth.name")} value={name}
                    onChange={(e) => setName(e.target.value)} className="h-12 pl-12 rounded-xl text-base" required data-testid="register-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="you@example.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} className="h-12 pl-12 rounded-xl text-base" required data-testid="register-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">{t("auth.password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)} className="h-12 pl-12 pr-12 rounded-xl text-base" minLength={6} required data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
                disabled={loading} data-testid="register-submit-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("auth.creatingAccount")}
                  </span>
                ) : (
                  <>{t("auth.register")}<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
              <div className="text-center text-sm text-muted-foreground pt-4">
                {t("auth.hasAccount")}{" "}
                <Link to="/login" className="text-primary hover:text-primary/80 font-semibold transition-colors" data-testid="login-link">
                  {t("auth.signIn")}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
